import React, { useEffect, useState } from "react";
import { PrintButton } from "../components/PrintButton";
import { BulkImportDialog } from "../components/BulkImportDialog";
import { getE911, createE911, updateE911, deleteE911, bulkCreateE911 } from "../lib/api";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Siren, ChevronRight, Plus, Trash2, Save, X, AlertTriangle, FileUp } from "lucide-react";

const STATUS = {
  compliant: { label: "Compliant", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20" },
  review: { label: "Needs Review", cls: "bg-amber-500/10 text-amber-400 border-amber-500/20" },
  pending: { label: "Pending", cls: "bg-blue-500/10 text-blue-400 border-blue-500/20" },
  "non-compliant": { label: "Non-Compliant", cls: "bg-red-500/10 text-red-400 border-red-500/20" },
};
const STATUSES = Object.keys(STATUS);

export default function E911() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});
  const [adding, setAdding] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [newRow, setNewRow] = useState({ name: "", erl: "", elin: "", location: "", provider: "", status: "pending" });

  const load = () => getE911().then((d) => { setRows(d); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const startEdit = (r) => { setEditId(r.id); setForm({ ...r }); };
  const saveEdit = async () => {
    try {
      const u = await updateE911(editId, {
        name: form.name, erl: form.erl || "", elin: form.elin || "",
        location: form.location || "", provider: form.provider || "", status: form.status,
      });
      setRows((p) => p.map((r) => (r.id === editId ? u : r)));
      setEditId(null);
      toast.success("Site updated");
    } catch { toast.error("Update failed"); }
  };
  const add = async () => {
    if (!newRow.name.trim()) { toast.error("Enter a site name"); return; }
    try {
      const c = await createE911(newRow);
      setRows((p) => [...p, c]);
      setAdding(false);
      setNewRow({ name: "", erl: "", elin: "", location: "", provider: "", status: "pending" });
      toast.success("Site added");
    } catch { toast.error("Add failed"); }
  };
  const remove = async (id) => {
    try { await deleteE911(id); setRows((p) => p.filter((r) => r.id !== id)); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const compliant = rows.filter((r) => r.status === "compliant").length;
  const gaps = rows.filter((r) => ["non-compliant", "review"].includes(r.status)).length;
  const pending = rows.filter((r) => r.status === "pending").length;
  const missingElin = rows.filter((r) => !r.elin).length;

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center px-5 sm:px-8 bg-zinc-950/60 backdrop-blur lg:sticky lg:top-0 z-10">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2">
          <span className="text-zinc-600">~/</span> e911 <ChevronRight className="h-3 w-3" /> compliance
        </span>
      </header>

      <div className="p-5 sm:p-8 max-w-6xl">
        <div className="no-print flex justify-end gap-2 mb-4">
          <button onClick={() => setShowImport(true)} data-testid="import-e911-btn" className="flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-md border border-zinc-700 text-zinc-300 hover:bg-zinc-800 transition-colors">
            <FileUp className="h-3.5 w-3.5" /> Import CSV
          </button>
          <PrintButton />
        </div>
        <BulkImportDialog
          open={showImport} onOpenChange={setShowImport} testid="import-e911"
          title="Bulk import E911 records"
          description="Upload a CSV or paste rows to add ERL/ELIN emergency-calling records in bulk."
          columns={[
            { key: "name", synonyms: ["site"] },
            { key: "erl", synonyms: ["emergency response location"] },
            { key: "elin", synonyms: ["did", "callback", "callback number"] },
            { key: "location", synonyms: ["address", "civic", "dispatchable location"] },
            { key: "provider", synonyms: ["carrier"] },
            { key: "status", synonyms: [] },
            { key: "notes", synonyms: ["note", "comment"] },
          ]}
          sampleCsv={"name,erl,elin,location,provider,status,notes\nHQ Floor 1,ERL-HQ-1,+13125550101,123 Main St Chicago IL,Intrado,compliant,\nBranch Dallas,ERL-DAL,+12145550102,500 Elm St Dallas TX,Bandwidth,review,"}
          sampleFilename="e911_template.csv"
          onImport={bulkCreateE911}
          onDone={load}
        />
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-red-400 font-semibold">
          <Siren className="h-4 w-4" /> E911 Compliance Tracker
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-white tracking-tight mt-2">Emergency Calling</h1>
        <p className="text-zinc-400 mt-3 max-w-2xl">
          Track emergency-calling readiness per site — Emergency Response Location (ERL), ELIN callback number,
          and the dispatchable civic address delivered to the PSAP. Required for RAY BAUM'S Act &amp; Kari's Law compliance.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
          <Stat label="Compliant" value={compliant} testId="e911-stat-compliant" tone={compliant > 0 ? "green" : "default"} />
          <Stat label="Gaps" value={gaps} testId="e911-stat-gaps" tone={gaps > 0 ? "red" : "default"} />
          <Stat label="Pending" value={pending} testId="e911-stat-pending" tone={pending > 0 ? "amber" : "default"} />
          <Stat label="Missing ELIN" value={missingElin} testId="e911-stat-missing" tone={missingElin > 0 ? "amber" : "default"} />
        </div>

        {(gaps > 0 || missingElin > 0) && (
          <div data-testid="e911-alert-banner" className="mt-4 flex items-start gap-2.5 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3">
            <AlertTriangle className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />
            <div className="text-[13px] text-red-200">
              <span className="font-semibold">{gaps + missingElin} site{gaps + missingElin > 1 ? "s" : ""}</span> need attention — missing ELIN/ERL or dispatchable location. Non-compliant sites can block 911 dispatch.
            </div>
          </div>
        )}

        <div className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 overflow-hidden">
          <div className="hidden md:grid grid-cols-[1.1fr_0.9fr_0.8fr_1.6fr_1fr_0.9fr_auto] gap-3 px-4 py-2.5 border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
            <span>Site</span><span>ERL</span><span>ELIN</span><span>Dispatchable Location</span><span>Provider</span><span>Status</span><span></span>
          </div>

          {loading ? (
            <div className="p-6 text-zinc-500 font-mono text-sm">Loading sites…</div>
          ) : (
            <div className="divide-y divide-zinc-800" data-testid="e911-list">
              {rows.map((r) => {
                const editing = editId === r.id;
                return (
                  <div key={r.id} data-testid={`e911-row-${r.id}`} className="grid grid-cols-2 md:grid-cols-[1.1fr_0.9fr_0.8fr_1.6fr_1fr_0.9fr_auto] gap-3 px-4 py-3 items-center">
                    {editing ? (
                      <>
                        <Input value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-8 bg-zinc-950 border-zinc-800 text-sm" />
                        <Input value={form.erl || ""} onChange={(e) => setForm({ ...form, erl: e.target.value })} placeholder="ERL" className="h-8 bg-zinc-950 border-zinc-800 text-sm" />
                        <Input data-testid={`edit-e911-elin-${r.id}`} value={form.elin || ""} onChange={(e) => setForm({ ...form, elin: e.target.value })} placeholder="ELIN" className="h-8 bg-zinc-950 border-zinc-800 text-sm" />
                        <Input value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Civic address" className="h-8 bg-zinc-950 border-zinc-800 text-sm" />
                        <Input value={form.provider || ""} onChange={(e) => setForm({ ...form, provider: e.target.value })} placeholder="Provider" className="h-8 bg-zinc-950 border-zinc-800 text-sm" />
                        <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                          <SelectTrigger data-testid={`edit-e911-status-${r.id}`} className="h-8 bg-zinc-950 border-zinc-800 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">{STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS[s].label}</SelectItem>)}</SelectContent>
                        </Select>
                        <div className="flex items-center gap-1.5">
                          <button onClick={saveEdit} data-testid={`save-e911-${r.id}`} className="h-8 w-8 flex items-center justify-center rounded-md bg-blue-600 hover:bg-blue-700 text-white"><Save className="h-4 w-4" /></button>
                          <button onClick={() => setEditId(null)} className="h-8 w-8 flex items-center justify-center rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-300"><X className="h-4 w-4" /></button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-sm text-white font-medium">{r.name}</div>
                        <div className="text-[13px] text-zinc-300 font-mono">{r.erl || "—"}</div>
                        <div className={`text-[13px] font-mono ${r.elin ? "text-zinc-300" : "text-amber-400"}`}>{r.elin || "—"}</div>
                        <div className="text-[13px] text-zinc-400 truncate">{r.location || "—"}</div>
                        <div className="text-[13px] text-zinc-400">{r.provider || "—"}</div>
                        <div><span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full border ${STATUS[r.status]?.cls || ""}`}>{STATUS[r.status]?.label || r.status}</span></div>
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => startEdit(r)} data-testid={`edit-e911-${r.id}`} className="text-[11px] text-blue-400 hover:underline">Edit</button>
                          <button onClick={() => remove(r.id)} data-testid={`delete-e911-${r.id}`} className="h-7 w-7 flex items-center justify-center rounded-md text-zinc-500 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {adding ? (
            <div className="p-4 border-t border-zinc-800 bg-zinc-950/50" data-testid="add-e911-form">
              <div className="grid grid-cols-1 sm:grid-cols-6 gap-2">
                <Input data-testid="new-e911-name" value={newRow.name} onChange={(e) => setNewRow({ ...newRow, name: e.target.value })} placeholder="Site name" className="h-9 bg-zinc-900 border-zinc-800 text-sm" />
                <Input value={newRow.erl} onChange={(e) => setNewRow({ ...newRow, erl: e.target.value })} placeholder="ERL" className="h-9 bg-zinc-900 border-zinc-800 text-sm" />
                <Input data-testid="new-e911-elin" value={newRow.elin} onChange={(e) => setNewRow({ ...newRow, elin: e.target.value })} placeholder="ELIN" className="h-9 bg-zinc-900 border-zinc-800 text-sm" />
                <Input value={newRow.location} onChange={(e) => setNewRow({ ...newRow, location: e.target.value })} placeholder="Civic address" className="sm:col-span-2 h-9 bg-zinc-900 border-zinc-800 text-sm" />
                <Select value={newRow.status} onValueChange={(v) => setNewRow({ ...newRow, status: v })}>
                  <SelectTrigger className="h-9 bg-zinc-900 border-zinc-800 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">{STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS[s].label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <button onClick={() => setAdding(false)} className="px-3 py-1.5 rounded-md bg-zinc-800 hover:bg-zinc-700 text-sm">Cancel</button>
                <button onClick={add} data-testid="confirm-add-e911" className="px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium">Add Site</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setAdding(true)} data-testid="add-e911-btn" className="w-full flex items-center justify-center gap-2 px-4 py-3 border-t border-zinc-800 text-sm text-zinc-400 hover:text-white hover:bg-zinc-800/40 transition-colors">
              <Plus className="h-4 w-4" /> Add site
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value, testId, tone = "default" }) => {
  const cls = tone === "red" ? "text-red-400" : tone === "amber" ? "text-amber-400" : tone === "green" ? "text-emerald-400" : "text-white";
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4" data-testid={testId}>
      <div className={`font-heading text-3xl font-extrabold ${cls}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mt-1">{label}</div>
    </div>
  );
};
